import { memo, useCallback, useMemo } from 'react';

import { ConfirmationModal } from '@air/component-confirmation-modal';

import { useCleanupImportPlaceholders } from '~/hooks/useCleanupImportPlaceholders';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { makeImporterTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { useCancelImportMutation } from '~/swr-hooks/importer/useCancelImportMutation';
import { useAirSelector } from '~/utils/ReduxUtils';
import { useCancelTask } from '~/utils/taskUtils/useCancelTask';

export type ImportCancellationModalProps = {
  importId: string;
};

export const ImportCancellationModal = memo(({ importId, onClose }: AirModalProps<ImportCancellationModalProps>) => {
  const { mutation } = useCancelImportMutation();
  const { currentWorkspace } = useCurrentWorkspace();
  const tasksSelector = useMemo(
    () => makeImporterTasksSelectorByWorkspaceId(currentWorkspace?.id),
    [currentWorkspace?.id],
  );
  const importerTasks = useAirSelector(tasksSelector);
  const { cancelTask } = useCancelTask();
  const { cleanupPlaceholders } = useCleanupImportPlaceholders();

  const handleConfirm = useCallback(async () => {
    const localTask = importerTasks.find((task) => task.localTaskId === importId);
    if (localTask?.remoteTaskId) {
      await mutation.mutateAsync(localTask?.remoteTaskId);
    }
    cleanupPlaceholders(importId);
    if (localTask) {
      cancelTask(localTask.localTaskId);
    }
    onClose();
  }, [mutation, importId, onClose, cancelTask, cleanupPlaceholders, importerTasks]);

  return (
    <ConfirmationModal
      id="import-cancellation-modal"
      isOpen
      isDangerous
      isLoading={mutation.isPending}
      onConfirm={handleConfirm}
      onDismiss={onClose}
      title="Cancel import?"
      description="This will stop any additional files and folders from being imported to Air, but it will not delete anything that has already been imported."
      ctaPrimaryText="Cancel import"
      ctaSecondaryText="Keep importing"
    />
  );
});

ImportCancellationModal.displayName = 'ImportCancellationModal';
